import React, { useRef, useState } from 'react';
import { Box } from '../types';
import { parseCSV, convertToBoxes, generateSampleCSV } from '../utils/fileUpload';

interface FileUploadProps {
  onAddBoxes: (boxes: Box[]) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onAddBoxes }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadStatus({ type: null, message: '' });

    try {
      const text = await file.text();
      const csvRows = parseCSV(text);
      const boxes = convertToBoxes(csvRows);
      
      onAddBoxes(boxes);
      setUploadStatus({
        type: 'success',
        message: `Successfully uploaded ${boxes.length} box types with ${boxes.reduce((sum, box) => sum + box.quantity, 0)} total boxes`
      });
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to upload file'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadSample = () => {
    const sampleCSV = generateSampleCSV();
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_boxes.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Upload CSV File</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV file with your box data. The file should include columns for Product ID, length, width, height, and quantity.
        </p>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4">
          <h4 className="font-medium text-blue-900 mb-2">CSV Format:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Headers:</strong> product_id,length,width,height,quantity</p>
            <p><strong>Example:</strong> B08N5WRWNW,30,20,15,10</p>
            <p><strong>Note:</strong> Product ID is optional but recommended for tracking</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
            disabled={isUploading}
          />
          <button
            type="button"
            onClick={handleDownloadSample}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
          >
            Download Sample
          </button>
        </div>

        {uploadStatus.type && (
          <div className={`p-4 rounded-lg ${
            uploadStatus.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <p className="text-sm font-medium">{uploadStatus.message}</p>
          </div>
        )}

        {isUploading && (
          <div className="flex items-center space-x-2 text-blue-600">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm">Processing file...</span>
          </div>
        )}
      </div>
    </div>
  );
}; 